import React, { useRef } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import { StreamingProviders } from '@core/dashboard/providers/MytaverseEventProvider/interfaces';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useStyles } from './styles';
import useAsyncEffect from 'use-async-effect';
import { useMytaverse } from '@providers/MytaverseProvider';
import { colors } from '@constants/colors';

const ConfigTab = () => {
  const {
    initMessageHandler,
    streamingProvider,
    currentRoom,
    currentRegion,
    gameliftStreamsSessionKey,
  } = useMytaverseEvent();
  const { connectedPod } = useMytaverseEvent();
  const classes = useStyles();
  const {
    conferenceName,
    isLimitedScreenResolution,
    setIsLimitedScreenResolution,
  } = useCommunications();
  const currentInitMessage = useRef('');
  const { isDebugMode, setIsDebugMode } = useMytaverse();

  // Building an INIT payload without a `sessionKey` MINTS a new websocket session key and makes it the hub's
  // current one (MytaverseEventProvider.generateNewGameliftStreamsSessionKey) — that is the connect path's job.
  // This tab only shows the payload, so it passes the live key: opening the Developers Terminal mid-visit used to
  // re-key the session under the running UE, after which every message from it was dropped by the sessionKey
  // filter — hundreds of "Skip handle WS message" lines, and, because the participant's position updates ride
  // that same filter, a `lastParticipantPosition` frozen at the moment the terminal was opened (2026-09-17).
  useAsyncEffect(async () => {
    if (!initMessageHandler || currentInitMessage.current) {
      return;
    }
    const message = await initMessageHandler({
      sessionKey: gameliftStreamsSessionKey,
    });

    if (!message) {
      return;
    }

    currentInitMessage.current = JSON.stringify(message);
  }, [initMessageHandler, gameliftStreamsSessionKey]);

  const handleCopy = () => {
    navigator.clipboard.writeText(currentInitMessage.current);
  };

  return (
    <Box sx={classes.root} className="no-drag">
      <FormControlLabel
        sx={{ color: colors.white }}
        control={
          <Checkbox
            checked={isDebugMode}
            onChange={() => {
              localStorage.setItem('isDebugMode', String(!isDebugMode));
              setIsDebugMode(!isDebugMode);
            }}
            sx={{ color: colors.white }}
          />
        }
        label="Editor Debug Mode"
      />
      <FormControlLabel
        sx={{ color: colors.white }}
        control={
          <Checkbox
            checked={isLimitedScreenResolution}
            onChange={() => {
              setIsLimitedScreenResolution(!isLimitedScreenResolution);
            }}
            sx={{ color: colors.white }}
          />
        }
        label="Zoom Limited Resolution"
      />
      <Typography>Streaming Provider: {streamingProvider}</Typography>
      <Typography>Current Room: {currentRoom?.roomId || '---'}</Typography>
      <Typography>Current Region: {currentRegion?.region || '---'} </Typography>
      <Typography>Zoom Conference: {conferenceName || '---'}</Typography>
      {streamingProvider === StreamingProviders.CoreWeave && (
        <Typography>Connected Pod: {connectedPod || '---'}</Typography>
      )}
      <Typography>
        Init Message
        <IconButton
          sx={{ cursor: 'pointer', color: 'white' }}
          onClick={handleCopy}
        >
          <ContentCopyIcon />
        </IconButton>
        : {currentInitMessage.current || '---'}
      </Typography>
    </Box>
  );
};

export default ConfigTab;
